import {
  Button,
  useModalState,
  Text,
  IconSignOut,
  Heading,
  csx,
} from '@vtex/admin-ui'
import { appStateSelector } from '@/checkout-instore-legacy/device/selectors'
import React, { useCallback, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import BottomModal from '@/checkout-instore-legacy/transaction/modal/BottomModal'
import { cancelTransaction } from '@/checkout-instore-legacy/transaction/redux/actions'
import { sendEvent } from '@/checkout-instore-legacy/utils/WebViewBridge'
import { FormattedMessage } from 'react-intl'

import { useAppStateShortCircuit } from './useAppStateShortCircuit'

export interface PaymentRedirectProps {
  redirectUrl: string
}

function redirect(url: string) {
  sendEvent({
    type: 'redirect',
    uri: url,
  })
}

const PaymentRedirect = ({ redirectUrl }: PaymentRedirectProps) => {
  const [inGracePeriod, setInGracePeriod] = useState(true)
  const modalState = useModalState()
  const appState = useSelector(appStateSelector)
  const dispatch = useDispatch()

  // Workaround for modals work properly until admin-ui fixes it!
  // https://vtex.slack.com/archives/C01DVTFA4VA/p1698084391075899
  useEffect(() => {
    modalState.setOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useAppStateShortCircuit()

  useEffect(function startGracePeriodTimeout() {
    setInGracePeriod(true)

    const timeout = setTimeout(() => {
      setInGracePeriod(false)
    }, 3000)

    return () => clearTimeout(timeout)
  }, [])

  useEffect(
    function redirectOnMount() {
      const isActiveState = appState === 'active'

      if (isActiveState) {
        redirect(redirectUrl)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [redirectUrl]
  )

  const handleRedirectClick = useCallback(() => {
    redirect(redirectUrl)
  }, [redirectUrl])

  const handleCancel = useCallback(() => {
    dispatch(cancelTransaction())
  }, [dispatch])

  return (
    <BottomModal state={modalState}>
      <div
        className={csx({
          display: 'flex',
          flexDirection: 'column',
          padding: '24px',
        })}
        data-cy="payment-redirect-modal"
      >
        <Heading>
          <FormattedMessage id="redirect.modal.title" />
        </Heading>
        <div className={csx({ display: 'flex', flexDirection: 'row' })}>
          <Text>
            <FormattedMessage id="redirect.modal.description" />
          </Text>
          <Button
            icon={<IconSignOut />}
            loading={inGracePeriod}
            onClick={handleRedirectClick}
            className={csx({
              borderRadius: '9999px',
              minWidth: '40px',
              minHeight: '40px',
            })}
          />
        </div>
        <Button
          className={csx({ marginTop: '32px' })}
          variant="criticalSecondary"
          onClick={handleCancel}
        >
          <FormattedMessage id="redirect.modal.cancel" />
        </Button>
      </div>
    </BottomModal>
  )
}

export default PaymentRedirect
